/**
 * What the route flies through, grouped by leg.
 *
 * One row per crossing, and the row is read left to right in the order a pilot
 * asks the questions: **which kind** (the dot and the abbreviation), **what is
 * it called**, **which class**, **between which levels**. `auf Karte` paints the
 * area on the canvas and flies to the point the route enters it — the one place
 * in this pane that touches the map, through the bridge, so no map internals
 * reach here.
 *
 * The dot is never alone: the kind is spelled out beside it wherever the
 * abbreviation is not self-explanatory (`R` → Beschränkungsgebiet), and the
 * class is the word `Klasse` plus its letter.
 */

import { useT } from '../../i18n';
import type { AirspaceLeg, AirspaceRow } from './airspace';

export function AirspaceList({
  legs,
  names,
  selected,
  onShow,
}: {
  legs: readonly AirspaceLeg[];
  /** the waypoint names, in route order, for the group headings */
  names: readonly string[];
  /** the crossing currently painted on the map, or '' */
  selected: string;
  onShow(row: AirspaceRow): void;
}) {
  const t = useT();
  const withRows = legs.filter(l => l.rows.length > 0);

  if (withRows.length === 0) {
    return <p className="aet-clear">{t('terrain.as.clear')}</p>;
  }

  return (
    <div className="aet-as">
      {withRows.map(l => (
        <div className="aet-as-leg" key={l.leg}>
          <h4 className="ae-eyebrow">
            {t('terrain.leg', {
              n: l.leg,
              from: names[l.leg - 1] ?? '',
              to: names[l.leg] ?? '',
            })}
          </h4>
          <ul>
            {l.rows.map(r => (
              <li key={r.id} className={'aet-as-row' + (selected === r.id ? ' on' : '')}>
                <span className={'aet-dot aet-' + r.group} aria-hidden="true" />
                {/* the name is what a pilot says on the radio, so it leads */}
                <span className="aet-as-name">{r.name || r.abbr || r.kind}</span>
                <button
                  type="button"
                  className="ae-btn ae-btn-quiet aet-as-show"
                  onClick={() => onShow(r)}
                >
                  {t('terrain.as.show')}
                </button>
                <span className="aet-as-detail">
                  {r.abbr && r.name ? <b>{r.abbr}</b> : null}
                  {r.kindKey ? <span>{t(r.kindKey)}</span> : null}
                  {r.icaoClass ? <span>{t('terrain.as.class', { c: r.icaoClass })}</span> : null}
                  <span className="ae-num">{r.limits}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
